import { createContext, useContext, useEffect, useState } from 'react'
import { collection, doc, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore'
import {
  COLLECTIONS,
  CONFIG_DOC_ID,
  EventConfigSchema,
  isActiveParticipant,
  ParticipantSchema,
  searchKeyOf,
  type EventConfig,
  type Participant,
} from '@jaikrob/shared'
import { db } from '@/lib/firebase'

export type Loadable<T> =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: T }

/**
 * Live, because the organiser closing the event mid-session should grey the button
 * out rather than let someone reach a server rejection at the payment step.
 * Parsed with the same schema the server uses, so a malformed config fails here
 * rather than as a wrong total further down.
 *
 * Private: one subscription per caller is one listener per caller, and the config is
 * a single document every screen wants. `EventConfigProvider` calls this once and
 * `useEventConfig` reads the result, so adding a consumer costs nothing.
 */
const useEventConfigSubscription = (): Loadable<EventConfig> => {
  const [value, setValue] = useState<Loadable<EventConfig>>({ state: 'loading' })

  useEffect(() => {
    return onSnapshot(
      doc(db, COLLECTIONS.config, CONFIG_DOC_ID),
      (snapshot) => {
        if (!snapshot.exists()) {
          setValue({ state: 'error', message: 'ยังไม่ได้ตั้งค่างานนี้ กรุณาติดต่อผู้จัดงาน' })
          return
        }
        const parsed = EventConfigSchema.safeParse(snapshot.data())
        setValue(
          parsed.success
            ? { state: 'ready', data: parsed.data }
            : { state: 'error', message: 'ข้อมูลงานไม่ถูกต้อง กรุณาติดต่อผู้จัดงาน' },
        )
      },
      () => setValue({ state: 'error', message: 'โหลดข้อมูลงานไม่สำเร็จ' }),
    )
  }, [])

  return value
}

const EventConfigContext = createContext<Loadable<EventConfig> | null>(null)

/**
 * Wraps the routes so every screen shares ONE config listener.
 *
 * Before this, each screen that wanted the event called the hook and opened its own
 * `onSnapshot` on the same document — two at a time once the header wanted the event
 * name as well. The provider also means a rename in /admin/settings reaches the
 * header, the totals and the size table together rather than screen by screen.
 */
export const EventConfigProvider = ({ children }: { children: React.ReactNode }) => (
  <EventConfigContext.Provider value={useEventConfigSubscription()}>
    {children}
  </EventConfigContext.Provider>
)

/** Reads the shared config. Throws rather than hanging on 'loading' if unwrapped. */
export const useEventConfig = (): Loadable<EventConfig> => {
  const value = useContext(EventConfigContext)
  if (!value) throw new Error('useEventConfig ต้องอยู่ใต้ <EventConfigProvider>')
  return value
}

/**
 * Fetched once, not subscribed: the roster does not change during a checkout, and
 * a live listener on a few thousand names is a cost the buyer's phone pays for
 * nothing.
 */
export const useParticipants = (): Loadable<Participant[]> => {
  const [value, setValue] = useState<Loadable<Participant[]>>({ state: 'loading' })

  useEffect(() => {
    let alive = true
    /*
     * Removed names are filtered here rather than in the query.
     *
     * `where('deletedAt', '==', null)` looks like the right answer and returns nothing:
     * Firestore does not match null-valued fields by equality, so the filter silently
     * empties the roster — which the picker then shows as "your name is not on the
     * list". Filtering after the read costs one pass over a few hundred rows and is
     * the behaviour we can actually verify.
     *
     * A removed name is refused by `createOrder` regardless, so this is about not
     * walking the buyer through five steps toward a dead end.
     */
    getDocs(query(collection(db, COLLECTIONS.participants), orderBy('name')))
      .then((snapshot) => {
        if (!alive) return
        const rows = snapshot.docs.flatMap((docSnap) => {
          const parsed = ParticipantSchema.safeParse({ id: docSnap.id, ...docSnap.data() })
          return parsed.success && isActiveParticipant(parsed.data) ? [parsed.data] : []
        })
        setValue({ state: 'ready', data: rows })
      })
      .catch(() => alive && setValue({ state: 'error', message: 'โหลดรายชื่อไม่สำเร็จ' }))
    return () => {
      alive = false
    }
  }, [])

  return value
}

/** Same normalisation the roster was written with, so accents and spaces do not matter. */
export const filterParticipants = (rows: Participant[], term: string): Participant[] => {
  const key = searchKeyOf(term)
  if (!key) return rows
  return rows.filter((row) => row.searchKey.includes(key))
}
